import { useSyncExternalStore, type ReactNode } from 'react';
import ReactGridLayout, {
  useContainerWidth,
  verticalCompactor,
  type Layout,
} from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import './dashboardGrid.css';
import { DashboardWidgetHandleContext, WIDGET_DRAG_HANDLE_CLASS } from './WidgetCardHeader';
import {
  DASHBOARD_GRID_COLS,
  DASHBOARD_GRID_GAP,
  DASHBOARD_MAX_ROWS,
  DASHBOARD_ROW_HEIGHT,
  keysInReadingOrder,
  type WidgetBox,
} from './dashboardWidgets';

const WIDE_QUERY = '(min-width: 1024px)';

/** Vrai sur grand écran : la grille déplaçable ne s'affiche qu'à partir de 1024 px. */
function useIsWide(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mq = window.matchMedia(WIDE_QUERY);
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    },
    () => window.matchMedia(WIDE_QUERY).matches,
  );
}

function boxesFromLayout(layout: Layout): Record<string, WidgetBox> {
  return Object.fromEntries(layout.map((it) => [it.i, { x: it.x, y: it.y, w: it.w, h: it.h }]));
}

export interface WidgetGridItem {
  key: string;
  minSpan: number;
  minRows: number;
}

/**
 * Grille de blocs déplaçables et redimensionnables (tableau de bord, statistiques).
 * Sous 1024 px et à l'impression, les blocs passent l'un sous l'autre.
 */
export function WidgetGrid({
  items,
  boxes,
  onCommit,
  render,
}: {
  items: WidgetGridItem[];
  boxes: Record<string, WidgetBox>;
  onCommit: (boxes: Record<string, WidgetBox>) => void;
  render: (key: string) => ReactNode;
}) {
  const isWide = useIsWide();
  const { width, containerRef, mounted } = useContainerWidth();

  const stack = (className: string) => (
    <div className={className}>
      {keysInReadingOrder(boxes).map((key) => (
        <div key={key} className="print:break-inside-avoid">{render(key)}</div>
      ))}
    </div>
  );

  if (!isWide) return stack('flex flex-col gap-6');

  const layout: Layout = items.flatMap((it) => {
    const box = boxes[it.key];
    return box
      ? [{ i: it.key, ...box, minW: it.minSpan, minH: it.minRows, maxH: DASHBOARD_MAX_ROWS }]
      : [];
  });

  const commit = (next: Layout) => onCommit(boxesFromLayout(next));

  return (
    <>
      <div ref={containerRef} className="cime-dashboard-grid print:hidden">
        {mounted && (
          <ReactGridLayout
            width={width}
            layout={layout}
            gridConfig={{
              cols: DASHBOARD_GRID_COLS,
              rowHeight: DASHBOARD_ROW_HEIGHT,
              margin: [DASHBOARD_GRID_GAP, DASHBOARD_GRID_GAP],
              containerPadding: [0, 0],
            }}
            dragConfig={{ handle: `.${WIDGET_DRAG_HANDLE_CLASS}` }}
            resizeConfig={{ handles: ['e', 's', 'se'] }}
            compactor={verticalCompactor}
            onDragStop={commit}
            onResizeStop={commit}
          >
            {items.map((it) => (
              <div
                key={it.key}
                className="[&>*]:h-full [&>*>:last-child]:overflow-y-auto [&_.cime-widget-list]:max-h-none"
              >
                <DashboardWidgetHandleContext.Provider value>
                  {render(it.key)}
                </DashboardWidgetHandleContext.Provider>
              </div>
            ))}
          </ReactGridLayout>
        )}
      </div>
      {stack('hidden print:flex print:flex-col print:gap-3')}
    </>
  );
}
